import useSWR, { SWRResponse } from 'swr'
import { useEffect, useState, useRef, useMemo } from 'react'
import { Typography, Box, IconButton, styled, Button } from '@mui/material'
import { IconChevronRight, IconChevronLeft } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import BoxWrapper from '../../../components/BoxWrapper'
import Breadcrumbs from '../../../components/ui/Breadcrumbs'
import Loading from '../../../components/Loading'
import axiosInstance from '../../../utils/axiosInstance'
import ReadOrderItem from './components/ReadOrderItem'
import EditOrderItem from './components/EditOrderItem'
import VacancyPage from '../../Vacancy'
import LeadsByOrder from './LeadsByOrder'
import OrderAnalytics from './OrderAnalytics'
import SocialAd from './SocialAd'
import CampaignSetup from './CampaignSetup'
import useInternalUserPermissions from '../../../hooks/useInternalUserPermissions'
import { PAGE, USER_ROLES } from '../../../constants'
import { useSelector } from '../../../store/store'

const TabContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  marginBottom: theme.spacing(3), // increased margin to create more separation
  padding: '12px 40px', // added vertical padding
  borderRadius: theme.shape.borderRadius,
}))

const TabsWrapper = styled(Box)({
  display: 'flex',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  overflowX: 'auto', // allow horizontal scroll
  scrollbarWidth: 'none', // hide Firefox scrollbar
  '&::-webkit-scrollbar': {
    // hide Chrome scrollbar
    display: 'none',
  },
  padding: '4px 0', // add some vertical padding
})

const TabButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  padding: '12px 16px',
  cursor: 'pointer',
  borderRadius: '4px',
  backgroundColor: active ? theme.palette.secondary.light : '#ffffff', // Changed to white
  color: active ? theme.palette.text.primary : theme.palette.text.secondary, // Changed to gray for non-active
  fontWeight: active ? 'bold' : 'normal',
  flex: active ? 3 : 1,
  minWidth: active ? '220px' : '150px',
  margin: '0 8px',
  textAlign: 'center',
  transition: 'all 0.2s ease-in-out',
  boxShadow: active ? '0 2px 6px rgba(0,0,0,0.1)' : 'none', // shadow only for active tab
  '&:hover': {
    backgroundColor: active ? theme.palette.secondary.light : '#f7f7f7', // Lighter hover effect
  },
}))

const LeftNavArrow = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: '#ffffff', // Changed to white background
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  left: 0,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.8)', // Slightly transparent white on hover
  },
}))

const RightNavArrow = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: '#ffffff', // Changed to white background
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  right: 0,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.8)', // Slightly transparent white on hover
  },
}))

const OrderDetailsHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1),
}))

// Base tabs available to all users
const BASE_ORDER_TABS = [
  { id: 0, label: 'Order Details', path: '' },
  { id: 1, label: 'Leads', path: '/leads' },
  { id: 2, label: 'Analytics', path: '/analytics' },
  { id: 3, label: 'Job Landingpage', path: '/application' },
] as const

// Admin-only tabs
const ADMIN_TABS = [
  { id: 4, label: 'Campaign Setup', path: '/campaign-setup' },
  { id: 5, label: 'Social Ad', path: '/social-ad' },
] as const

const OrderItem = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()

  const [activeTab, setActiveTab] = useState(0)
  const [isEditActive, setIsEditActive] = useState(false)
  const tabsRef = useRef<HTMLDivElement>(null)
  const { isInternalUser } = useInternalUserPermissions()
  const { role } = useSelector((state) => state?.user.userInfo)

  // Check if user is admin
  const isAdmin = role === USER_ROLES.ADMIN.value

  // Check if user can edit orders (internal users or external customers who own the order)
  const isExternalCustomer = [
    USER_ROLES.EXTERNAL_DIRECT_CUSTOMER.value,
    USER_ROLES.EXTERNAL_RESELLER_CUSTOMER.value,
    USER_ROLES.EXTERNAL_RESELLER.value,
  ].includes(role || '')

  const canEditOrder = isInternalUser || isExternalCustomer

  // Build tabs based on user role
  const ORDER_TABS = useMemo(() => {
    type Tab = { id: number; label: string; path: string }
    const tabs: Tab[] = [...BASE_ORDER_TABS]
    if (isAdmin) {
      tabs.push(...ADMIN_TABS)
    }
    return tabs
  }, [isAdmin])

  // Fetch order data using SWR
  const {
    data: fetchedOrderDetails,
    isLoading: loadingTaskDetails,
    mutate: refetchOrder,
  }: SWRResponse = useSWR(`/orders/${id}`, axiosInstance)
  const order = fetchedOrderDetails?.data?.order

  // Create custom navigation with order name - ensure it always matches current URL
  const customNavigation = useMemo(() => {
    // Determine the correct URL for the breadcrumb based on current location
    const currentPath = location.pathname

    // Detect which base route we're on: /orders or /order
    const isOrdersRoute = currentPath.startsWith(PAGE.ORDERS.PATH)
    const basePath = isOrdersRoute ? PAGE.ORDERS.PATH : PAGE.ORDER.PATH
    const baseOrderPath = `${basePath}/${id}`

    // Use the current path if we're in a sub-route, otherwise use base path
    const breadcrumbUrl = currentPath.startsWith(baseOrderPath) ? currentPath : baseOrderPath

    return {
      items: [
        {
          id: 'order-nav',
          title: 'Order Navigation',
          type: 'group',
          children: [
            {
              id: 'orders',
              title: 'Orders',
              type: 'item',
              url: PAGE.ORDERS.PATH,
              breadcrumbs: true,
            },
            {
              id: 'order-detail',
              title: order?.jobName || `Order #${order?.displayId || id}`,
              type: 'item',
              url: breadcrumbUrl,
              breadcrumbs: true,
            },
          ],
        },
      ],
    }
  }, [order?.jobName, order?.displayId, id, location.pathname])

  const handleTabChange = (tabId: number) => {
    setActiveTab(tabId)
    const selectedTab = ORDER_TABS[tabId]

    navigate(`/order/${id}${selectedTab.path}`)
  }

  const handlePrevious = () => {
    const newTabIndex = activeTab > 0 ? activeTab - 1 : ORDER_TABS.length - 1
    handleTabChange(newTabIndex)
  }

  const handleNext = () => {
    const newTabIndex = activeTab < ORDER_TABS.length - 1 ? activeTab + 1 : 0
    handleTabChange(newTabIndex)
  }

  const renderTabContent = () => {
    // Loading state common for tabs that depend on order data
    if (loadingTaskDetails) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Loading />
        </Box>
      )
    }

    switch (activeTab) {
      case 0: {
        if (order) {
          return (
            <Box sx={{ width: '960px', maxWidth: '100%', margin: '0 auto' }}>
              <OrderDetailsHeader>
                <Typography variant="h4" component="h1">
                  {isEditActive ? (
                    <FormattedMessage id="edit-order" />
                  ) : (
                    <FormattedMessage id="order-details" />
                  )}
                </Typography>
                {canEditOrder && (
                  <Box>
                    {isEditActive ? (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setIsEditActive(false)
                          navigate({ search: '' })
                        }}
                        size="large"
                      >
                        <FormattedMessage id="cancel" />
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                          setIsEditActive(true)
                        }}
                        size="large"
                      >
                        <FormattedMessage id="edit" />
                      </Button>
                    )}
                  </Box>
                )}
              </OrderDetailsHeader>
              {isEditActive ? (
                <EditOrderItem
                  order={order}
                  onCancel={() => setIsEditActive(false)}
                  onSave={() => {
                    setIsEditActive(false)
                    navigate({ search: '' })
                    refetchOrder()
                  }}
                />
              ) : (
                <ReadOrderItem order={order} />
              )}
            </Box>
          )
        }
        return (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1">No order found</Typography>
          </Box>
        )
      }
      case 1:
        return (
          <Box sx={{ p: 2, width: '100%' }}>
            <LeadsByOrder orderId={id} hideWrapper hideHeader />
          </Box>
        )
      case 2:
        return (
          <Box sx={{ p: 2, width: '100%' }}>
            <OrderAnalytics orderId={id} />
          </Box>
        )
      case 3: {
        if (order) {
          return (
            <Box
              sx={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '0 auto' }}
            >
              <VacancyPage isPreview orderId={order.id} />
            </Box>
          )
        }
        return (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1">No order found</Typography>
          </Box>
        )
      }
      case 4: {
        // Campaign Setup tab - only accessible by admins
        if (isAdmin) {
          return (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <CampaignSetup
                orderId={order?.id}
                orderDisplayId={order?.displayId}
                jobName={order?.jobName}
              />
            </Box>
          )
        }
        return (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1">Access denied</Typography>
          </Box>
        )
      }
      case 5: {
        // Social Ad tab - only accessible by admins
        if (order && isAdmin) {
          return <SocialAd orderId={order.id} hasAdDescription={!!order.adDescription} />
        }
        return (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body1">Access denied</Typography>
          </Box>
        )
      }
      default:
        return (
          <Box sx={{ p: 2, textAlign: 'center', maxWidth: '960px', margin: '0 auto' }}>
            <Typography variant="body1">No content available</Typography>
          </Box>
        )
    }
  }

  // Ensure the active tab is always centered
  useEffect(() => {
    if (tabsRef.current) {
      const activeElement = tabsRef.current.children[activeTab] as HTMLElement
      if (activeElement) {
        const scrollPosition =
          activeElement.offsetLeft - (tabsRef.current.offsetWidth - activeElement.offsetWidth) / 2
        tabsRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' })
      }
    }
  }, [activeTab])

  // Set active tab based on URL on component mount
  useEffect(() => {
    const path = location.pathname
    const tabPath = path.split(`/order/${id}`)[1] || ''

    const foundTab = ORDER_TABS.findIndex((tab) => tab.path === tabPath)
    if (foundTab >= 0) {
      setActiveTab(foundTab)
    } else {
      setActiveTab(0)
    }
  }, [location.pathname, id, ORDER_TABS])

  useEffect(() => {
    if (activeTab !== 0) return
    if (location.pathname.includes('/campaign-setup')) return
    if (isEditActive) {
      navigate({ search: 'edit' })
    } else if (window.location.search === '?edit') {
      setIsEditActive(true)
    } else {
      navigate({ search: '' })
    }
  }, [isEditActive, activeTab, navigate, location.pathname])

  return (
    <>
      {/* Custom breadcrumb with order name */}
      <Breadcrumbs
        separator={IconChevronRight}
        navigation={customNavigation}
        icon
        title
        rightAlign
      />

      {/* Carousel section */}
      <TabContainer>
        <LeftNavArrow onClick={handlePrevious} aria-label="previous tab">
          <IconChevronLeft />
        </LeftNavArrow>

        <TabsWrapper ref={tabsRef}>
          {ORDER_TABS.map((tab) => (
            <TabButton
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </TabButton>
          ))}
        </TabsWrapper>

        <RightNavArrow onClick={handleNext} aria-label="next tab">
          <IconChevronRight />
        </RightNavArrow>
      </TabContainer>

      {/* Content area */}
      <BoxWrapper
        height="100%"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {renderTabContent()}
      </BoxWrapper>
    </>
  )
}

export default OrderItem
